import React, {Fragment, useEffect, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import type {CancelOrderResult, PaymentGroup, UnifiedOrder} from '../services/adminOrdersService';
import Badge from '@/shared/components/Badge';
import {dollars} from '@/shared/utils/money';

const MEMBERSHIP_SOURCES = new Set(['membership', 'membership_recurring']);

/** The consequence line under the controls — states what happens to the
 *  My Labs+ membership for the CURRENT scope/refund selection, or null when
 *  no membership is affected. */
export function membershipConsequence(args: {
    group: PaymentGroup;
    scope: 'order' | 'purchase';
    refund: boolean;
    currentOrderId: string;
}): string | null {
    const m = args.group.membership;
    if (!m || m.status === 'cancelled') return null;
    const membershipOrder = args.group.orders.find((o) => MEMBERSHIP_SOURCES.has(o.source));
    if (!membershipOrder) return null;
    const inScope = args.scope === 'purchase' || membershipOrder.id === args.currentOrderId;
    if (!inScope) return null;
    const alreadyRefunded =
        membershipOrder.payment_allocated_cents > 0 &&
        membershipOrder.refund_amount_cents >= membershipOrder.payment_allocated_cents;
    if (args.refund || alreadyRefunded) {
        return `My Labs+ membership ends immediately (${dollars(m.amount_cents)} fee refunded).`;
    }
    return `My Labs+ membership auto-renewal stops; access continues until ${m.current_period_end.slice(0, 10)}.`;
}

/** One toast line out of either cancel response shape. */
export function summarizeCancelResult(result: CancelOrderResult): string {
    if ('results' in result) {
        const cancelled = result.results.filter((r) => r.outcome === 'cancelled');
        const skipped = result.results.filter((r) => r.outcome === 'skipped');
        const failed = result.results.filter((r) => r.outcome === 'failed');
        const refundedCents = cancelled.reduce((sum, r) => sum + (r.refund?.refundedCents ?? 0), 0);
        const parts = [`Cancelled ${cancelled.length} item${cancelled.length === 1 ? '' : 's'}`];
        if (refundedCents > 0) parts.push(`refunded ${dollars(refundedCents)}`);
        if (result.membership.outcome === 'deactivated') parts.push('membership deactivated');
        if (result.membership.outcome === 'renewal_stopped') parts.push('membership auto-renew stopped');
        if (result.membership.outcome === 'left_active') parts.push('membership left active (order cancel failed)');
        let message = `${parts.join(', ')}.`;
        if (skipped.length > 0) {
            message += ` ${skipped.length} skipped (${skipped.map((s) => s.reason ?? 'not cancellable').join('; ')}).`;
        }
        if (failed.length > 0) {
            message += ` ${failed.length} FAILED (${failed.map((f) => f.reason ?? 'unknown error').join('; ')}).`;
        }
        // An order that cancelled but couldn't refund carries a reason. Without
        // this the money simply doesn't appear in the refund total and the
        // admin has no way to tell it was left at the provider.
        const notRefunded = cancelled.filter((r) => r.reason);
        if (notRefunded.length > 0) {
            message += ` ⚠️ NOT REFUNDED: ${notRefunded.map((r) => `${r.order_number} — ${r.reason}`).join('; ')}`;
        }
        return message;
    }
    const parts = ['Order cancelled'];
    if (result.refund) parts.push(`refunded ${dollars(result.refund.refundedCents)}`);
    if (result.membership?.outcome === 'deactivated') parts.push('membership deactivated');
    if (result.membership?.outcome === 'renewal_stopped') parts.push('membership auto-renew stopped');
    return `${parts.join(', ')}.`;
}

export interface CancelOrderDialogProps {
    open: boolean;
    order: UnifiedOrder;
    pending: boolean;
    onClose: () => void;
    onConfirm: (payload: {scope: 'order' | 'purchase'; refund: boolean; reason?: string}) => void;
}

const CancelOrderDialog: React.FC<CancelOrderDialogProps> = ({open, order, pending, onClose, onConfirm}) => {
    const [scope, setScope] = useState<'order' | 'purchase'>('order');
    const [refund, setRefund] = useState(true);
    const [reason, setReason] = useState('');

    // The dialog stays mounted between opens, so without this the radio,
    // refund checkbox, and reason from a prior open would leak into the
    // next one. Reset to defaults every time it opens.
    useEffect(() => {
        if (open) {
            setScope('order');
            setRefund(true);
            setReason('');
        }
    }, [open]);

    const group = order.payment_group;
    const hasGroup = group != null && (group.orders.length > 1 || group.membership != null);
    const cancellableCount = hasGroup ? group.orders.filter((o) => o.cancellable.ok).length : 0;
    const purchaseTotal = hasGroup
        ? group.orders.filter((o) => o.cancellable.ok).reduce((sum, o) => sum + o.payment_allocated_cents, 0)
        : 0;
    const effectiveScope = hasGroup ? scope : 'order';
    const consequence = hasGroup
        ? membershipConsequence({group, scope: effectiveScope, refund, currentOrderId: order.id})
        : null;

    const confirm = () => {
        onConfirm({scope: effectiveScope, refund, reason: reason.trim() === '' ? undefined : reason.trim()});
    };

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog as="div" className="relative z-[60]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-150"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-150"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-100"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-lg rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                                <div className="border-b border-gray-200 px-6 py-4">
                                    <Dialog.Title className="text-lg font-semibold text-mld-text-primary">
                                        Cancel order {order.order_number}
                                    </Dialog.Title>
                                </div>
                                <div className="px-6 py-5 space-y-4">
                                    {hasGroup && (
                                        <div>
                                            <p className="text-sm text-gray-500 mb-2">
                                                This payment ({dollars(group.amount_cents)}) also covers:
                                            </p>
                                            <ul className="space-y-1">
                                                {group.orders.map((o) => (
                                                    <li key={o.id} className="flex items-center gap-2 text-sm">
                                                        <span className="text-mld-text-primary">{o.order_number}</span>
                                                        <span className="text-gray-500">
                                                            {dollars(o.payment_allocated_cents)}
                                                        </span>
                                                        {o.is_current && <Badge variant="info">this order</Badge>}
                                                        {!o.cancellable.ok && (
                                                            <span className="text-gray-500 text-xs">
                                                                {o.cancellable.reason}
                                                            </span>
                                                        )}
                                                    </li>
                                                ))}
                                                {group.membership && (
                                                    <li className="flex items-center gap-2 text-sm">
                                                        <span className="text-mld-text-primary">
                                                            My Labs+ membership ({group.membership.plan})
                                                        </span>
                                                        <span className="text-gray-500">
                                                            {dollars(group.membership.amount_cents)}
                                                        </span>
                                                        <Badge
                                                            variant={
                                                                group.membership.status === 'active'
                                                                    ? 'success'
                                                                    : 'neutral'
                                                            }>
                                                            {group.membership.status}
                                                        </Badge>
                                                    </li>
                                                )}
                                            </ul>
                                            <fieldset className="mt-3 space-y-1">
                                                <label className="flex items-center gap-2 text-sm text-mld-text-primary">
                                                    <input
                                                        type="radio"
                                                        name="cancel-scope"
                                                        checked={scope === 'order'}
                                                        onChange={() => setScope('order')}
                                                    />
                                                    This item only
                                                </label>
                                                <label className="flex items-center gap-2 text-sm text-mld-text-primary">
                                                    <input
                                                        type="radio"
                                                        name="cancel-scope"
                                                        checked={scope === 'purchase'}
                                                        onChange={() => setScope('purchase')}
                                                    />
                                                    Entire purchase ({cancellableCount} item
                                                    {cancellableCount === 1 ? '' : 's'}, {dollars(purchaseTotal)})
                                                </label>
                                            </fieldset>
                                        </div>
                                    )}
                                    <label className="flex items-center gap-2 text-sm text-mld-text-primary">
                                        <input
                                            type="checkbox"
                                            checked={refund}
                                            onChange={(e) => setRefund(e.target.checked)}
                                        />
                                        Refund the payment
                                    </label>
                                    <label className="block text-sm text-mld-text-primary">
                                        Reason (optional)
                                        <input
                                            type="text"
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        />
                                    </label>
                                    {consequence && <p className="text-sm text-mld-status-high">{consequence}</p>}
                                </div>
                                <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={pending}
                                        className="rounded-md border border-mld-panel-border px-4 py-2 text-sm font-semibold text-mld-text-secondary hover:bg-[var(--mld-background)] disabled:opacity-50">
                                        Keep order
                                    </button>
                                    <button
                                        type="button"
                                        onClick={confirm}
                                        disabled={pending}
                                        className="inline-flex items-center gap-2 rounded-md bg-mld-danger px-4 py-2 text-sm font-semibold text-white hover:bg-mld-danger/90 disabled:cursor-not-allowed disabled:opacity-50">
                                        Cancel order
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default CancelOrderDialog;
